import { Trend } from "@tandemdiabetes/sugarmate-api-react-client"
import classNames from "classnames"
import dayjs from "dayjs"
import { useMemo } from "react"

import styles from "./Reading.module.scss"
import { TrendIcon } from "src/components"
import { useFollowedUsers } from "src/contexts"
import {
  useReadableGlucoseUnit,
  useReadableGlucoseValue,
  useReadingDifference,
  useThisMinute,
} from "src/hooks"
import type { Reading } from "src/types"

interface Props {
  reading: Reading;
}

export function Reading(props: Props) {
  const { reading } = props

  const thisMinute = useThisMinute()
  const { currentDeviceSettings } = useFollowedUsers()
  const unit = useReadableGlucoseUnit()
  const value = useReadableGlucoseValue(
    reading.glucose.mg_dl,
    reading.glucose.special_value,
  )

  const readingDifference = useReadingDifference(reading)
  const showDifference = readingDifference && currentDeviceSettings.show_delta

  const isReadingNearlyStale = useMemo(
    () => dayjs(thisMinute).diff(
      reading.startedAt,
      "minute",
      true,
    ) >= 10,
    [
      reading,
      thisMinute,
    ],
  )

  return (
    <div
      className={classNames(
        styles.readingContainer,
        isReadingNearlyStale ? styles.readingContainerNearlyStale : styles.readingContainerCurrent,
      )}
    >
      <div
        className={styles.reading}
        id="cgm-reading-value"
      >
        {value}
      </div>
      <TrendIcon
        className={styles.trendIcon}
        trend={reading.glucose.trend ?? Trend.None}
        id="cgm-trend-arrow"
      />
      {showDifference && (
        <div className={styles.deltaContainer}>
          <div
            className={styles.delta}
            id="cgm-reading-delta"
          >
            {readingDifference}
          </div>
          <div
            className={styles.units}
            id="cgm-unit"
          >
            {unit}
          </div>
        </div>
      )}
    </div>
  )
}
